import { useState, useCallback, useMemo } from 'react';
import { ILoyaltyProgramViewDTO } from '../../../libs/coreapi-dto/dirs/loyaltyProgram';
import { ILoyaltyProgramParams, ILoyaltyProgramSettings, IParsedLoyaltyProgram } from './interface';

export const useLoyaltyProgram = (initialProgram: ILoyaltyProgramViewDTO) => {
    const [program, setProgram] = useState<ILoyaltyProgramViewDTO>(initialProgram);

    const parsedProgram = useMemo((): IParsedLoyaltyProgram => {
        try {
            const parsedSettings: ILoyaltyProgramSettings = JSON.parse(program.settings || '{}');
            const parsedParams: ILoyaltyProgramParams = JSON.parse(program.params || '{}');

            // Функция для проверки пустых значений
            const isEmpty = (value: any): boolean => {
                return value === null || value === undefined || value === '' || value === '""' || value === "''";
            };

            return {
                ...program,
                parsedSettings,
                parsedParams: {
                    ...parsedParams,
                    // Для SmsTemplate - сообщение о подтверждении регистрации
                    SmsTemplate: isEmpty(parsedParams.SmsTemplate) 
                        ? 'Для подтверждения регистрации и согласия на получение уведомлений используйте смс код $[sms_code]'
                        : parsedParams.SmsTemplate,
                    // Для SmsValidationTemplate - сообщение об изменении данных
                    SmsValidationTemplate: isEmpty(parsedParams.SmsValidationTemplate)
                        ? 'Получен запрос на изменение данных. Код: $[sms_code]'
                        : parsedParams.SmsValidationTemplate,
                    // Для SmsDebitTemplate - сообщение о списании баллов
                    SmsDebitTemplate: isEmpty(parsedParams.SmsDebitTemplate)
                        ? 'Для подтверждения согласия на списание баллов используйте код $[sms_code]'
                        : parsedParams.SmsDebitTemplate
                }
            };
        } catch (error) {
            console.error(error);
            return {
                ...program,
                parsedSettings: {
                    Url: '',
                    Name: '',
                    Proxy: { Use: false, Port: 0 },
                    PinCode: '',
                    Timeout: 0,
                    CardPrefix: '',
                    StoreDepartmentId: '',
                    StoreDepartmentKey: '',
                    IsAutoSendAllCheques: false
                },
                parsedParams: {
                    SmsTemplate: 'Для подтверждения регистрации и согласия на получение уведомлений используйте смс код $[sms_code]',
                    IDActionGroup: '',
                    MinSumForDebit: 0,
                    DebitPermitBySms: false,
                    SmsDebitTemplate: 'Для подтверждения согласия на списание баллов используйте код $[sms_code]',
                    SmsValidationTemplate: 'Получен запрос на изменение данных. Код: $[sms_code]',
                    UsePersonalAdditionSale: false
                }
            };
        }
    }, [program]);

    // Базовые функции обновления
    const updateProgramField = useCallback(<K extends keyof ILoyaltyProgramViewDTO>(
        field: K,
        value: ILoyaltyProgramViewDTO[K]
    ) => {
        setProgram(prev => ({ ...prev, [field]: value }));
    }, []);

    const updateProgramName = (name: string) => updateProgramField('name', name);
    const updateProgramType = (type: number) => updateProgramField('type', type);
    const updateContractorIdGlobal = (idContractorGlobal: string) => updateProgramField('idContractorGlobal', idContractorGlobal);
    const updateProgramEnabled = (isEnabled: boolean) => updateProgramField('isEnabled', isEnabled);

    // Функции для обновления settings
    const updateSettingsField = useCallback(<K extends keyof ILoyaltyProgramSettings>(
        field: K,
        value: ILoyaltyProgramSettings[K]
    ) => {
        setProgram(prev => {
            const currentSettings = JSON.parse(prev.settings || '{}');
            const newSettings = { ...currentSettings, [field]: value };
            return { ...prev, settings: JSON.stringify(newSettings) };
        });
    }, []);

    const updateParamsField = useCallback(<K extends keyof ILoyaltyProgramParams>(
        field: K,
        value: ILoyaltyProgramParams[K]
    ) => {
        setProgram(prev => {
            const currentParams = JSON.parse(prev.params || '{}');
            const newParams = { ...currentParams, [field]: value };
            return { ...prev, params: JSON.stringify(newParams) };
        });
    }, []);

    // Конкретные функции для settings
    const updateUrl = (url: string) => updateSettingsField('Url', url);
    const updatePinCode = (pinCode: string) => updateSettingsField('PinCode', pinCode);
    const updateCardPrefix = (cardPrefix: string) => updateSettingsField('CardPrefix', cardPrefix);
    const updateStoreDepartmentId = (id: string) => updateSettingsField('StoreDepartmentId', id);
    const updateStoreDepartmentKey = (key: string) => updateSettingsField('StoreDepartmentKey', key);
    const updateTimeout = (timeout: number) => updateSettingsField('Timeout', timeout);
    const updateIsAutoSendAllCheques = (value: boolean) => updateSettingsField('IsAutoSendAllCheques', value);

    // Функции для Proxy
    const updateProxyUse = (use: boolean) => updateSettingsField('Proxy', {
        ...parsedProgram.parsedSettings.Proxy,
        Use: use
    });

    const updateProxyPort = (port: number) => updateSettingsField('Proxy', {
        ...parsedProgram.parsedSettings.Proxy,
        Port: port
    });
    const updateProxyAddress = (address: string) => updateSettingsField('Proxy', {
        ...parsedProgram.parsedSettings.Proxy,
        Address: address
    });
    const updateProxyUser = (user: string) => updateSettingsField('Proxy', {
        ...parsedProgram.parsedSettings.Proxy,
        User: user
    });
    const updateProxyPassword = (password: string) => updateSettingsField('Proxy', {
        ...parsedProgram.parsedSettings.Proxy,
        Password: password
    });

    // Функции для params
    const updateSmsTemplate = (template: string) => updateParamsField('SmsTemplate', template);
    const updateSmsValidationTemplate = (template: string) => updateParamsField('SmsValidationTemplate', template);
    const updateSmsDebitTemplate = (template: string) => updateParamsField('SmsDebitTemplate', template);
    const updateIdActionGroup = (id: string) => updateParamsField('IDActionGroup', id);
    const updateMinSumForDebit = (sum: number) => updateParamsField('MinSumForDebit', sum);
    const updateDebitPermitBySms = (permit: boolean) => updateParamsField('DebitPermitBySms', permit);
    const updateUsePersonalAdditionSale = (use: boolean) => updateParamsField('UsePersonalAdditionSale', use);

    const updateCompatibility = (compatibility: boolean) => updateProgramField('compatibility', compatibility);
    
    // Функции для совместимости
    const setIncompatibleActions = useCallback((actionIds: string[]) => {
        setProgram(prev => ({
            ...prev,
            incompatibleDiscount2Program: actionIds
        }));
    }, []);

    const setIncompatibleTypeCards = useCallback((typeCardIds: string[]) => {
        setProgram(prev => ({
            ...prev,
            incompatibleDiscount2CardType: typeCardIds
        }));
    }, []);

    const setIncompatiblePrograms = useCallback((programIds: string[]) => {
        setProgram(prev => ({
            ...prev,
            incompatibleLoyaltyProgram: programIds
        }));
    }, []);


    return {
        program,
        parsedProgram,
        updateProgramName,
        updateProgramType,
        updateContractorIdGlobal,
        updateProgramEnabled,
        updateUrl,
        updatePinCode,
        updateCardPrefix,
        updateStoreDepartmentId,
        updateStoreDepartmentKey,
        updateTimeout,
        updateIsAutoSendAllCheques,
        updateProxyUse,
        updateProxyPort,
        updateProxyAddress,
        updateProxyUser,
        updateProxyPassword,
        updateSmsTemplate,
        updateSmsValidationTemplate,
        updateSmsDebitTemplate,
        updateIdActionGroup,
        updateMinSumForDebit,
        updateDebitPermitBySms,
        updateUsePersonalAdditionSale,
        updateCompatibility,
        setIncompatibleActions,
        setIncompatibleTypeCards,
        setIncompatiblePrograms
    };
};